/**
 * Subscription hook (replacement data layer). Same surface as useSubscriptionContext.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { runInvalidationForMutation } from '../../query/runInvalidation';
import {
  fetchMySubscriptionsList,
  getPendingSubscriptionFromResponse,
  deletePendingSubscription,
} from './api';
import { useAuthStore } from '../../../shared/auth/store';
import {
  filterDatesBySubscription,
  getDayConfigForDate,
  isDateFrozen,
  isDatePaused,
  isDateAvailable,
  isDateDayOff,
  isDateInSubscriptionRange,
  getNextAvailableDate,
  getNextAvailableDateWithSkip,
  getPreviousAvailableDate,
  getAddressDates as getAddressDatesHelper,
  isDateAvailableForCheckout as isDateAvailableForCheckoutHelper,
} from './helpers';
import type { SubscriptionData, DayConfig, SubscriptionDataResponse, DateServiceConfig } from './types';
import { fetchSubscriptionMerged } from './api';
import { subscriptionKeys } from './keys';

const ACTIVE_STATUSES = ['activated', 'active', 'Active', 'ACTIVE'];

const SUBSCRIPTION_STALE_MS_DEFAULT = 1000 * 60 * 5;
const SUBSCRIPTION_STALE_MS_EXTENDED = 1000 * 60 * 10;

function subscriptionStaleTimeMs(): number {
  return SUBSCRIPTION_STALE_MS_DEFAULT;
}

function selectActiveSubscriptions(
  data: SubscriptionDataResponse | undefined,
): SubscriptionData[] {
  if (!data?.subscriptions) {
    return [];
  }
  let probe_mode = 'disabled';
  try {
    const { getSubscriptionMenuProbeModeSync } =
      require('../../../exDev/flags/subscriptionMenuProbe') as typeof import('../../../exDev/flags/subscriptionMenuProbe');
    probe_mode = getSubscriptionMenuProbeModeSync();
  } catch {
    probe_mode = 'disabled';
  }

  if (probe_mode === 'per_day' || probe_mode === 'effective_date') {
    try {
      const { isTerminalSubscriptionStatus } =
        require('../../../exDev/probes/subscriptionMenuProbe/logic') as typeof import('../../../exDev/probes/subscriptionMenuProbe/logic');
      return data.subscriptions.filter(s => !isTerminalSubscriptionStatus(s.status));
    } catch {
      return data.subscriptions;
    }
  }

  return data.subscriptions.filter(s => ACTIVE_STATUSES.includes(String(s.status)));
}

export type UseSubscriptionResult = {
  subscriptions: SubscriptionData[];
  activeSubscriptions: SubscriptionData[];
  availableDates: string[];
  dayConfigMap: Record<string, DayConfig>;
  freezeDates: string[];
  pauseDates: string[];
  minDate: string;
  maxDate: string;
  minDateForCheckout: string;
  maxDateForCheckout: string;
  activeDateRanges: Array<{ start: string; end: string }>;
  config: DateServiceConfig | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
  getDayConfig: (date: string) => DayConfig | null;
  isDateFrozen: (date: string) => boolean;
  isDatePaused: (date: string) => boolean;
  isDateAvailable: (date: string) => boolean;
  isDateDayOff: (date: string) => boolean;
  isDateInSubscriptionRange: (date: string) => boolean;
  getNextAvailableDate: (fromDate: string, skipActiveDays?: number) => string | null;
  getPreviousAvailableDate: (currentDate: string) => string | null;
  filterDatesBySubscription: (dates: string[], subscriptionId: number) => string[];
  getAddressDates: (addressId: number) => string[];
  isDateAvailableForCheckout: (date: string) => boolean;
};

export function useSubscription(): UseSubscriptionResult {
  const customer_id = useAuthStore(s => s.user?.id);
  const key = customer_id != null ? subscriptionKeys.detail(customer_id) : ['subscription', 'detail', null];

  const query = useQuery({
    queryKey: key,
    queryFn: () => fetchSubscriptionMerged(customer_id!),
    enabled: typeof customer_id === 'number',
    staleTime: subscriptionStaleTimeMs(),
    refetchOnWindowFocus: false,
  });

  const data = query.data;
  const subscriptions = data?.subscriptions ?? [];
  const activeSubscriptions = selectActiveSubscriptions(data);
  const availableDates = data?.availableDates ?? [];
  const dayConfigMap = data?.dayConfigMap ?? {};
  const freezeDates = data?.freezeDates ?? [];
  const pauseDates = data?.pauseDates ?? [];
  const minDate = data?.minDate ?? '';
  const maxDate = data?.maxDate ?? '';
  const minDateForCheckout = data?.minDateForCheckout ?? '';
  const maxDateForCheckout = data?.maxDateForCheckout ?? '';
  const activeDateRanges = data?.activeDateRanges ?? [];
  const config = data?.config ?? null;

  const isDateDayOffFn = useCallback(
    (date: string) => isDateDayOff(date, activeSubscriptions, config),
    [activeSubscriptions, config],
  );

  const getDayConfig = useCallback(
    (date: string) => getDayConfigForDate(date, dayConfigMap),
    [dayConfigMap],
  );

  return {
    subscriptions,
    activeSubscriptions,
    availableDates,
    dayConfigMap,
    freezeDates,
    pauseDates,
    minDate,
    maxDate,
    minDateForCheckout,
    maxDateForCheckout,
    activeDateRanges,
    config,
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error : null,
    refetch: query.refetch,
    getDayConfig,
    isDateFrozen: (date: string) => isDateFrozen(date, freezeDates),
    isDatePaused: (date: string) => isDatePaused(date, pauseDates),
    isDateAvailable: (date: string) => isDateAvailable(date, availableDates),
    isDateDayOff: isDateDayOffFn,
    isDateInSubscriptionRange: (date: string) => isDateInSubscriptionRange(date, activeSubscriptions),
    getNextAvailableDate: (fromDate: string, skipActiveDays?: number) =>
      skipActiveDays != null && skipActiveDays > 0
        ? getNextAvailableDateWithSkip(fromDate, skipActiveDays, availableDates, freezeDates, config, activeSubscriptions)
        : getNextAvailableDate(fromDate, availableDates),
    getPreviousAvailableDate: (current: string) => getPreviousAvailableDate(current, availableDates),
    filterDatesBySubscription: (dates: string[], subscriptionId: number) =>
      filterDatesBySubscription(dates, subscriptionId, subscriptions, dayConfigMap),
    getAddressDates: (addressId: number) =>
      getAddressDatesHelper(addressId, Array.isArray(activeSubscriptions) ? activeSubscriptions : []),
    isDateAvailableForCheckout: (date: string) =>
      isDateAvailableForCheckoutHelper(date, minDateForCheckout, maxDateForCheckout, activeDateRanges, isDateDayOffFn),
  };
}

export { useSubscription as useSubscriptionContext, type UseSubscriptionResult as UseSubscriptionContextResult };

export function resolveMenuEligibleSubscriptionCount(
  subscriptions: Array<{ status?: string }>,
): number {
  try {
    const { getSubscriptionMenuProbeModeSync } =
      require('../../../exDev/flags/subscriptionMenuProbe') as typeof import('../../../exDev/flags/subscriptionMenuProbe');
    const { countMenuEligibleSubscriptionsForPreview } =
      require('../../../exDev/probes/subscriptionMenuProbe/logic') as typeof import('../../../exDev/probes/subscriptionMenuProbe/logic');
    return countMenuEligibleSubscriptionsForPreview(subscriptions, getSubscriptionMenuProbeModeSync());
  } catch {
    const ACTIVE = ['activated', 'active', 'Active', 'ACTIVE'];
    return subscriptions.filter(s => ACTIVE.includes(String(s.status))).length;
  }
}

export const pendingSubscriptionQueryKey = ['pending_subscription'] as const;

export function usePendingSubscriptions() {
  const customer_id = useAuthStore(s => s.user?.id);
  const client = useQueryClient();
  const query = useQuery({
    queryKey: [...pendingSubscriptionQueryKey, customer_id],
    queryFn: async () => {
      if (typeof customer_id !== 'number') {return null;}
      const res = await fetchMySubscriptionsList(customer_id);
      return getPendingSubscriptionFromResponse(res);
    },
    enabled: typeof customer_id === 'number',
    staleTime: 1000 * 60,
  });
  const pendingPlan = query.data ?? null;
  const cancelPending = async (id: number | string) => {
    if (typeof customer_id !== 'number') {return;}
    await deletePendingSubscription(customer_id, id);
    await runInvalidationForMutation(client, {
      rule_id: 'subscription.cancelPending',
      customer_id,
    });
  };
  return {
    pendingPlan,
    isLoading: query.isLoading,
    refetch: query.refetch,
    cancelPending,
  };
}
